import { createSelector } from '@reduxjs/toolkit';

import {
  setAutocompleteResponse,
  setDefaultAutocompleteSuggestions,
  sendAutocompleteRequest,
  setAutocompleteQuery,
} from 'Core/actions/autocomplete.ts';
import { dialogOpened } from 'Core/actions/dialog';
import { defaultAutocompleteResponseState } from 'Core/reducers/autocomplete/response';
import {
  autocompleteDefaultSuggetionsSelector,
  autocompleteResponseItemsSelector,
  autocompleteRequestQuerySelector,
} from 'Core/selectors/autocomplete.ts';
import { autocompletePreselectionSelector } from 'Core/selectors/preselection.js';
import { AutocompleteItem, FacetValueBase } from 'Models/index.ts';
import requestConfig from 'Models/uiConfig/requestConfig.js';
import { autocomplete, abortControllerCreator } from 'Modules/serverApi/index.js';

import type { Dispatch } from '@reduxjs/toolkit';
import type { EpicArg } from 'Core/middlewares/epics.ts';
import type { AutocompleteItemType } from 'Models/autocompleteItem.ts';
import type { ServerModel } from 'Modules/serverApi/types.ts';

const types: AutocompleteItemType[] = requestConfig.autocomplete.order;

const createAbortController = abortControllerCreator();

const isRedirectItem = (item: ServerModel.AutocompleteItem): item is ServerModel.AutocompleteProductItem =>
  item.Type === 'Product';

const requestSelector = createSelector(
  autocompleteRequestQuerySelector,
  autocompletePreselectionSelector,
  (query: string, selection: FacetValueBase[]) => ({ query, selection }),
);

export default async function ({
  dispatch,
  state,
  action: { payload, type },
}: EpicArg): Promise<Dispatch | void> {
  const isInputTrigged = type === setAutocompleteQuery.type;
  const isActionTrigged = type === sendAutocompleteRequest.type;

  const isDialogJustOpened = type === dialogOpened.type && payload === 'SearchBoxDialog';
  const autocompleteResponseItems = autocompleteResponseItemsSelector(state) as AutocompleteItem[];
  const isDialogTriggered = isDialogJustOpened && !autocompleteResponseItems?.length;

  if (!isInputTrigged && !isActionTrigged && !isDialogTriggered) {
    return;
  }

  const request = isActionTrigged ? payload : requestSelector(state);

  const isDefaultSuggestionsRequest = !request.query;
  const defaultSuggestion = autocompleteDefaultSuggetionsSelector(state);
  const allowToDefaultSuggestionRequest = isDefaultSuggestionsRequest && !defaultSuggestion;

  const selection = allowToDefaultSuggestionRequest ? { query: request.query } : request;

  if (allowToDefaultSuggestionRequest || request.query.length >= 3) {
    const isFilterWithinAutocomplete = isActionTrigged && !!request.query;

    try {
      const rawResponse = (await autocomplete(
        { ...selection, types },
        createAbortController(),
      )) as ServerModel.AutocompleteResponse;

      const responseItems = rawResponse.map((item) => new AutocompleteItem(item));

      if (isDefaultSuggestionsRequest) {
        dispatch(setDefaultAutocompleteSuggestions(responseItems));
      } else {
        // if performed filter within autocomplete, then update only product, do not update text or catagory suggestions
        // TODO: send 'type' in request to get only product, we don't need other types
        const items = isFilterWithinAutocomplete
          ? autocompleteResponseItems
              .filter((i) => i.type !== 'product')
              .concat(responseItems?.filter((i) => i.type === 'product') ?? [])
          : responseItems;

        const { TotalHits: totalHits = 0 } = rawResponse.find(isRedirectItem) || {};

        dispatch(setAutocompleteResponse({ items, totalHits, query: request.query }));
      }
    } catch {
      dispatch(setAutocompleteResponse(defaultAutocompleteResponseState));
    }
  }
}
